/**
 * Orphan inbox — slice 에 없으나 실제 데이터에 사용 중인 kind/domain/relation 을 모아 보여준다.
 *
 * "한 번에 추가" 를 누르면 draft 에 일괄 흡수한다 (useOntologyDraft.absorbOrphans).
 * 개별 추가도 지원 — 각 행의 + 버튼.
 */
import { useMemo } from "react"
import type { OntologySlice, OntologyUsage } from "../../api/admin-client.ts"

interface OrphanInboxProps {
	readonly slice: OntologySlice
	readonly usage: OntologyUsage
	readonly onAbsorbAll: () => void
	readonly onAbsorbOne: (kind: "kinds" | "domains" | "relations", name: string) => void
}

interface OrphanEntry {
	readonly name: string
	readonly count: number
}

function diff(usage: Record<string, number>, sliceNames: ReadonlyArray<string>): ReadonlyArray<OrphanEntry> {
	const sliceSet = new Set(sliceNames)
	const orphans: OrphanEntry[] = []
	for (const [name, count] of Object.entries(usage)) {
		if (!sliceSet.has(name) && count > 0) orphans.push({ name, count })
	}
	orphans.sort((a, b) => b.count - a.count)
	return orphans
}

export function OrphanInbox(props: OrphanInboxProps) {
	const kindOrphans = useMemo(
		() =>
			diff(
				props.usage.kinds,
				props.slice.kinds.map((k) => k.name)
			),
		[props.usage.kinds, props.slice.kinds]
	)
	const domainOrphans = useMemo(
		() =>
			diff(
				props.usage.domains,
				props.slice.domains.map((d) => d.name)
			),
		[props.usage.domains, props.slice.domains]
	)
	const relationOrphans = useMemo(
		() =>
			diff(
				props.usage.relations,
				props.slice.relations.map((r) => r.name)
			),
		[props.usage.relations, props.slice.relations]
	)

	const total = kindOrphans.length + domainOrphans.length + relationOrphans.length
	if (total === 0) return null

	return (
		<section className="ontology-section orphan-inbox">
			<div className="ontology-section-head">
				<h3 className="ontology-section-title">
					미등록 사용 항목 <span className="muted">({total})</span>
				</h3>
				<button type="button" className="btn btn-primary btn-sm" onClick={props.onAbsorbAll}>
					한 번에 추가
				</button>
			</div>
			<p className="muted ontology-section-hint">
				실제 데이터에는 있으나 슬라이스에 누락된 항목들. "한 번에 추가" 로 흡수하거나 개별 + 버튼으로 담을 수
				있습니다.
			</p>

			<div className="orphan-groups">
				<OrphanColumn title="종류" items={kindOrphans} onAdd={(name) => props.onAbsorbOne("kinds", name)} />
				<OrphanColumn
					title="도메인"
					items={domainOrphans}
					onAdd={(name) => props.onAbsorbOne("domains", name)}
				/>
				<OrphanColumn
					title="관계"
					items={relationOrphans}
					onAdd={(name) => props.onAbsorbOne("relations", name)}
				/>
			</div>
		</section>
	)
}

function OrphanColumn(props: {
	readonly title: string
	readonly items: ReadonlyArray<OrphanEntry>
	readonly onAdd: (name: string) => void
}) {
	return (
		<div className="orphan-column">
			<h4 className="orphan-column-title">
				{props.title} <span className="muted">({props.items.length})</span>
			</h4>
			{props.items.length === 0 ? (
				<p className="muted" style={{ fontSize: 12 }}>
					—
				</p>
			) : (
				<ul className="orphan-list">
					{props.items.map((item) => (
						<li key={item.name} className="orphan-row">
							<code className="inline">{item.name}</code>
							<span className="badge">{item.count}</span>
							<button type="button" className="btn btn-ghost btn-sm" onClick={() => props.onAdd(item.name)}>
								+ 추가
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
